import { Link, NavLink, useNavigate } from "react-router-dom";
import { Droplets, LogOut, LayoutList, Settings as SettingsIcon, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminShell({ children }) {
  const { state, logout } = useAuth();
  const navigate = useNavigate();

  async function onLogout() {
    await logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  }

  const emailDisabled = state && state.email_configured === false;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header
        data-testid="admin-header"
        className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-100 z-40"
      >
        <div className="max-w-5xl mx-auto h-full flex items-center justify-between px-4">
          <Link to="/admin" className="flex items-center gap-2 text-[#334E68]">
            <Droplets className="w-5 h-5 text-[#16A34A]" strokeWidth={2.5} />
            <span className="font-display text-base font-bold tracking-tight">
              Green Services · Admin
            </span>
          </Link>
          <button
            type="button"
            data-testid="admin-logout"
            onClick={onLogout}
            aria-label="Sign out"
            className="min-w-[44px] min-h-[44px] px-3 flex items-center gap-1.5 rounded-lg text-sm font-medium text-gray-600 hover:text-[#334E68] hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <main className="pt-16 pb-24">
        {emailDisabled && (
          <div
            data-testid="email-disabled-banner"
            className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs sm:text-sm"
          >
            <div className="max-w-5xl mx-auto px-4 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong>Email notifications: DISABLED.</strong> Add a Gmail app
                password to <code>SMTP_HOST</code>/<code>SMTP_USER</code>/
                <code>SMTP_PASS</code> in <code>/app/backend/.env</code> to turn
                these on. New-lead emails are being written to{" "}
                <code>lead_notifications.log</code> until then.
              </span>
            </div>
          </div>
        )}
        <div className="max-w-5xl mx-auto px-4 pt-4">{children}</div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav
        data-testid="admin-bottom-nav"
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-40"
      >
        <div className="max-w-5xl mx-auto grid grid-cols-2">
          <NavLink
            to="/admin"
            end
            data-testid="nav-leads"
            className={({ isActive }) =>
              [
                "min-h-[56px] flex items-center justify-center gap-2 text-sm font-medium",
                isActive
                  ? "text-[#16A34A] bg-green-50"
                  : "text-gray-500 hover:text-[#334E68]",
              ].join(" ")
            }
          >
            <LayoutList className="w-4 h-4" /> Leads
          </NavLink>
          <NavLink
            to="/admin/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              [
                "min-h-[56px] flex items-center justify-center gap-2 text-sm font-medium",
                isActive
                  ? "text-[#16A34A] bg-green-50"
                  : "text-gray-500 hover:text-[#334E68]",
              ].join(" ")
            }
          >
            <SettingsIcon className="w-4 h-4" /> Settings
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
